import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { PageHeader, Section, Empty, StatusBadge } from "@/components/kit";
import SecureImage from "@/components/SecureImage";
import useLoad from "@/lib/useLoad";
import api, { errMsg, fmtDate } from "@/lib/api";

const WEEK_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export async function uploadPhoto(file, pose = "front") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("pose", pose);
  const { data } = await api.post("/photos", fd);
  return data;
}

function FieldInput({ f, value, onChange }) {
  const tid = `checkin-field-${f.key}`;
  if (f.type === "text") return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />;
  if (f.type === "boolean") return <Switch checked={!!value} onCheckedChange={onChange} data-testid={tid} />;
  if (f.type === "scale") return (
    <div className="flex items-center gap-4"><Slider min={1} max={10} step={1} value={[value || 5]} onValueChange={([v]) => onChange(v)} data-testid={tid} />
      <span className="font-display text-2xl w-8">{value || 5}</span></div>
  );
  return <Input type="number" step="any" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={tid} />;
}

export default function CheckIn() {
  const [form] = useLoad("/checkin-form");
  const [history, reload] = useLoad("/checkins");
  const [answers, setAnswers] = useState({});
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const p = await uploadPhoto(file, "checkin"); setPhotos([...photos, p]); } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };
  const submit = async () => {
    setBusy(true);
    const filled = { ...answers };
    (form.fields || []).forEach((f) => { if (f.type === "scale" && filled[f.key] == null) filled[f.key] = 5; });
    try {
      await api.post("/checkins", { answers: filled, photo_ids: photos.map((p) => p.id) });
      toast.success("Check-in submitted successfully");
      setAnswers({}); setPhotos([]); reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader eyebrow={form && !Array.isArray(form) ? `Every ${WEEK_DAYS[form.due_day ?? 6]}` : "Check-in"} title="Check-in" testid="checkin-title" />
      <div className="grid lg:grid-cols-[1fr_380px] gap-6">
        <Section title="This week" testid="checkin-form">
          {!form || Array.isArray(form) ? <div className="eyebrow">Loading…</div> : (
            <div className="space-y-5">
              {form.fields.map((f) => (
                <div key={f.key}><Label className="mb-2 block">{f.label}{f.required && <span className="text-[#FF3B30]"> *</span>}</Label>
                  <FieldInput f={f} value={answers[f.key]} onChange={(v) => setAnswers({ ...answers, [f.key]: v })} /></div>
              ))}
              <div>
                <Label className="mb-2 block">Progress photos (private - only you and your coach)</Label>
                <div className="flex gap-2 flex-wrap">
                  {photos.map((p) => (
                    <div key={p.id} className="relative"><SecureImage photoId={p.id} className="w-20 h-28 object-cover rounded" />
                      <button className="absolute -top-2 -right-2 bg-black rounded-full p-0.5" onClick={() => setPhotos(photos.filter((x) => x.id !== p.id))}><X size={12} /></button></div>
                  ))}
                  <button onClick={() => fileRef.current.click()} className="w-20 h-28 border border-dashed border-slate-600 rounded grid place-items-center text-slate-500 hover:border-[#FF3B30] transition-colors" data-testid="checkin-add-photo-btn"><Camera size={18} /></button>
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} data-testid="check-in-photo-upload-input" />
                </div>
              </div>
              <Button onClick={submit} disabled={busy} className="h-11 px-8" data-testid="checkin-submit-btn">{busy ? "Submitting…" : "Submit check-in"}</Button>
            </div>
          )}
        </Section>
        <Section title="History" testid="checkin-history">
          {!history?.length ? <Empty text="No check-ins yet." /> : history.map((c) => (
            <div key={c.id} className="py-3 border-t border-[#1E293B]" data-testid={`checkin-history-${c.id}`}>
              <div className="flex justify-between text-sm"><span>{fmtDate(c.submitted_at)}</span><StatusBadge status={c.reviewed ? "reviewed" : "pending"} /></div>
              {c.coach_notes && <div className="text-sm text-slate-300 mt-2 border-l-2 border-[#FF3B30] pl-3" data-testid={`checkin-coach-notes-${c.id}`}>{c.coach_notes}</div>}
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}
